import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from 'app/store';
import { SearchResultsGroupMember } from 'features/searchMolecules/searchMoleculesSlice';
import last from 'lodash/last';
import remove from 'lodash/remove';
import FragnetService, { Supplier } from 'services/FragnetService';

// Types

export interface SupplierItem {
  supplier: string;
  code: string;
  displayCode: string;
  smiles: string;
}

export interface CartMember extends SearchResultsGroupMember {
  availability?: SupplierItem[];
  isLoading: boolean;
  selectedItems: SupplierItem[];
}

export interface Cart {
  cartId: number;
  items: CartMember[];
  name: string;
}

export interface ShoppingCartState {
  carts: Cart[];
  selectedCart: number;
}

type AvailabilityLoadingPayload = { cartId: number; cartMember: number };

export interface AvailabilitySuccessPayload extends AvailabilityLoadingPayload {
  availability: SupplierItem[];
}
export interface ToggleSelectedItemPayload extends AvailabilityLoadingPayload {
  item: SupplierItem;
}

export interface SetSelectedItemPayload extends ToggleSelectedItemPayload {
  selected: boolean;
}

// Utils

const getMemberToUpdate = (
  state: ShoppingCartState,
  action: PayloadAction<AvailabilityLoadingPayload>,
) => {
  const cartToUpdate = state.carts.find((c) => c.cartId === action.payload.cartId);
  if (cartToUpdate) {
    return cartToUpdate.items.find((m) => m.id === action.payload.cartMember);
  } else {
    throw new Error(`Cart with id ${action.payload.cartId} doesn't exist`);
  }
};

const getCartToUpdate = (carts: Cart[], selectedCardId: number) => {
  return carts.find((c) => c.cartId === selectedCardId)!;
};

const getLargestCartId = (carts: Cart[]) => {
  return Math.max(...carts.map((cart) => cart.cartId));
};

const regexTransform = (suppliers: Supplier[], supplier: string, code: string): string => {
  switch (supplier) {
    case 'MolPort':
      return code.replace(/MOLPORT:(.*)/, 'MolPort-$1');
    case 'ChemSpace-BB':
      return code.replace(/CHEMSPACE-BB:(.*)/, '$1');
    default:
      return 'code';
  }
};
// Slice

// one cart with empty array of items is created by default
const initialState: ShoppingCartState = {
  carts: [
    {
      cartId: 1,
      items: [],
      name: 'untitled',
    },
  ],
  selectedCart: 1,
};

export const shoppingCartSlice = createSlice({
  name: 'shoppingCart',
  initialState: initialState,
  reducers: {
    addNewCart(state, action: PayloadAction<string>) {
      const cartId = getLargestCartId(state.carts) + 1;
      state.carts.push({
        name: action.payload,
        cartId,
        items: [],
      });
      state.selectedCart = cartId;
    },
    addBatchToCart(state, action: PayloadAction<CartMember[]>) {
      const cartToUpdate = getCartToUpdate(state.carts, state.selectedCart);
      action.payload.forEach((member) => {
        const existingItem = cartToUpdate.items.find((molecule) => molecule.id === member.id);
        if (!existingItem) {
          cartToUpdate.items.push(member);
        }
      });
    },
    removeCart(state, action: PayloadAction<number>) {
      if (state.carts.length > 1) {
        state.carts = state.carts.filter((cart) => cart.cartId !== action.payload);
        if (state.selectedCart === action.payload) {
          state.selectedCart = last(state.carts)!.cartId;
        }
      }
    },
    removeBatchFromCart(state, action: PayloadAction<number[]>) {
      const cartToUpdate = getCartToUpdate(state.carts, state.selectedCart);
      action.payload.forEach((id) => {
        cartToUpdate.items = cartToUpdate.items.filter((item) => item.id !== id);
      });
    },
    addItemToCart(state, action: PayloadAction<CartMember>) {
      const cartToUpdate = getCartToUpdate(state.carts, state.selectedCart);
      const existingItem = cartToUpdate.items.find((molecule) => molecule.id === action.payload.id);
      if (!existingItem) {
        cartToUpdate.items.push(action.payload);
      }
    },
    removeFromCart(state, action: PayloadAction<number>) {
      const cartToUpdate = getCartToUpdate(state.carts, state.selectedCart);
      cartToUpdate.items = cartToUpdate.items.filter((item) => item.id !== action.payload);
    },
    selectCart(state, action: PayloadAction<number>) {
      state.selectedCart = action.payload;
    },
    getAvailabilityLoading(state, action: PayloadAction<AvailabilityLoadingPayload>) {
      const memberToUpdate = getMemberToUpdate(state, action);
      if (memberToUpdate) {
        memberToUpdate.isLoading = true;
      }
    },
    getAvailabilitySuccess(state, action: PayloadAction<AvailabilitySuccessPayload>) {
      const memberToUpdate = getMemberToUpdate(state, action);
      if (memberToUpdate) {
        memberToUpdate.isLoading = false;
        const { availability } = action.payload;

        memberToUpdate.availability = availability;
      }
    },
    getAvailabilityFailure(state, action: PayloadAction<AvailabilityLoadingPayload>) {
      const memberToUpdate = getMemberToUpdate(state, action);
      if (memberToUpdate) {
        memberToUpdate.isLoading = false;
      }
    },
    toggleSelectedItem(state, action: PayloadAction<ToggleSelectedItemPayload>) {
      const memberToUpdate = getMemberToUpdate(state, action);
      if (memberToUpdate) {
        const codes = memberToUpdate.selectedItems.map((item) => item.code);
        if (!codes.includes(action.payload.item.code)) {
          memberToUpdate.selectedItems.push(action.payload.item);
        } else {
          remove(
            memberToUpdate.selectedItems,
            (item: SupplierItem) => item.code === action.payload.item.code,
          );
        }
      }
    },
    setSelected(state, action: PayloadAction<SetSelectedItemPayload>) {
      const memberToUpdate = getMemberToUpdate(state, action);
      if (memberToUpdate) {
        const codes = memberToUpdate.selectedItems.map((item) => item.code);
        if (action.payload.selected) {
          if (!codes.includes(action.payload.item.code)) {
            memberToUpdate.selectedItems.push(action.payload.item);
          }
        } else {
          remove(
            memberToUpdate.selectedItems,
            (item: SupplierItem) => item.code === action.payload.item.code,
          );
        }
      }
    },
  },
});

export const {
  addNewCart,
  addBatchToCart,
  removeCart,
  removeBatchFromCart,
  addItemToCart,
  removeFromCart,
  selectCart,
  getAvailabilityLoading,
  getAvailabilitySuccess,
  getAvailabilityFailure,
  toggleSelectedItem,
  setSelected,
} = shoppingCartSlice.actions;

// Thunk Actions

export const getAvailabilityThunk = (
  cartId: number,
  cartMember: number,
  smiles: string,
): AppThunk<Promise<AvailabilitySuccessPayload>> => async (
  dispatch,
): Promise<AvailabilitySuccessPayload> => {
  const {
    getAvailabilityLoading,
    getAvailabilitySuccess,
    getAvailabilityFailure,
  } = shoppingCartSlice.actions;
  dispatch(getAvailabilityLoading({ cartId, cartMember }));
  try {
    const response = await FragnetService.getAvailability(smiles);
    const suppliers = await FragnetService.getSuppliers();
    return dispatch(
      getAvailabilitySuccess({
        cartId,
        cartMember,
        availability: response.data.items.map((item: SupplierItem) => ({
          ...item,
          displayCode: regexTransform(suppliers, item.supplier, item.code),
        })),
      }),
    ).payload;
  } catch (error) {
    dispatch(getAvailabilityFailure({ cartId, cartMember }));
    throw error;
  }
};

// Selectors

export const shoppingCartStateSelector = (state: RootState, ..._: any): ShoppingCartState =>
  state.shoppingCart;

export const getShoppingCartState = createSelector(shoppingCartStateSelector, (state) => state);
export const getCarts = createSelector(shoppingCartStateSelector, (state) => state.carts);
export const getSelectedCartId = createSelector(
  shoppingCartStateSelector,
  (state) => state.selectedCart,
);
export const getCurrentCart = createSelector(
  getCarts,
  getSelectedCartId,
  (carts, selectedCartId) => carts.find((cart: Cart) => cart.cartId === selectedCartId)!,
);

export const actions = {
  ...shoppingCartSlice.actions,
  getAvailabilityThunk,
};

// Export the reducer, either as a default or named export
export default shoppingCartSlice.reducer;
